import { useMutation } from "@apollo/client";
import { ADMIN_UPDATE_FESTIVAL_SETTINGS } from "../graphql";
import { useFieldArray, useForm } from "react-hook-form";
import {
  Button,
  Container,
  TextField,
} from "@mui/material";
import "react-image-crop/dist/ReactCrop.css";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import semver from "semver";
import { Setting } from "../../../__generated__/graphql";
import { ButtonContainer } from "../../../components/ButtonContainer";
import { PageContainer } from "../../../components/PageContainer";
import { triggerLiveEditionExport } from "../../../client";

const menuItems = ["VIP", "Map", "Debug", "Showcase"];

const schema = yup
  .object({
    minVersion: yup
      .string()
      .required()
      .test(
        "semver-valid",
        "is not a valid version number",
        (value) => !!semver.valid(value)
      ),
    downForMaintenance: yup.boolean().required(),
    editionId: yup.string().required(),
  })
  .required();

export interface ISettingsProps {
  setting: Setting;
  versionNumber: string;
}

export const SuperAdminSettingsForm = ({ setting, versionNumber }: ISettingsProps) => {
  const defaultCheckboxes = menuItems.map((item) => {
    const checked = setting.hiddenMenuItems?.includes(item);

    return {
      name: item,
      checked: !!checked,
    };
  });

  const [updateSettings] = useMutation(
    ADMIN_UPDATE_FESTIVAL_SETTINGS,
    {
      onCompleted: () => {
        toast("settings updated");
        // IMPORTANT BUG FIX: auto-export only ever exports the live edition, so
        // if next year's event was prepared in full before switching to it, no
        // export file exists yet. The app (via lambda) would then have nothing
        // to read once the edition is switched. Export on switch to guarantee it.
        void triggerLiveEditionExport({ suppressToast: true });
      },
    }
  );
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      downForMaintenance: setting.downForMaintenance,
      minVersion: setting.minVersion,
      editionId: setting.editionId,
    },
  });
  const onSubmit = (val: any) => {
    const {
      editionId,
      minVersion,
      downForMaintenance,
    } = val;

    updateSettings({
      variables: {
        editionId,
        minVersion,
        downForMaintenance,
      },
    });
  };

  return (
    <PageContainer title="Super Admin App Settings">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Container style={{ flexDirection: "column", display: "flex" }}>
          <TextField
            id="outlined-basic"
            label="Minimum version"
            variant="outlined"
            {...register("minVersion")}
          />
          <p>{errors.minVersion?.message}</p>
          <TextField
            id="outlined-basic"
            label="Down for maintenance"
            variant="outlined"
            // defaultValue={false}
            {...register("downForMaintenance")}
          />
          <p>{errors.downForMaintenance?.message}</p>        

          <ButtonContainer>
            <Button type="submit" variant="contained">
              Save
            </Button>
          </ButtonContainer>
        </Container>
      </form>

      <div style={{ paddingTop: 20 }}>Info:</div>
      <div>Env Name:{process.env.REACT_APP_ENV_NAME}</div>
      <div>Bucket: {process.env.REACT_APP_BUCKET_URL}</div>
      <div>API: {process.env.REACT_APP_API}</div>
      <div>Backend Version: {versionNumber}</div>

      <div style={{ paddingTop: 20 }}>Debug links:</div>
      <div style={{ display: "flex", flexDirection: "column" }}>
      <a href="ltd.meliora.atg://debug/true">ATG Debug</a>
      <a href="ltd.meliora.trees://debug/true">Trees Debug</a>
      <a href="ltd.meliora.damnation://debug/true">Damnation Debug</a>
      <a href="ltd.meliora.bloodstock://debug/true">Bloodstock Debug</a>
      <a href="ltd.meliora.radar://debug/true">Radar Debug</a>
      <a href="ltd.meliora.retrorides://debug/true">RetroRides Debug</a>
      </div>
    </PageContainer>
  );
};
